import { useCallback, useEffect, useRef } from "react";

type UseShiftSelectionManagerOptions<CollectionItem> = {
  collection?: Array<CollectionItem>;
  getId?: (item: CollectionItem) => string;
  selectedIds?: ReadonlySet<string>;
};

export type ShiftSelectionAction = (id: string) => string[];

const defaultGetId = (item: { id?: string }) => item.id!;

const resolveRangeStartIndex = (
  clickIndex: number,
  lastIndex: number,
  originIndex: number | undefined
) => {
  if (originIndex === undefined) {
    return lastIndex;
  }

  const lastSide = Math.sign(lastIndex - originIndex);
  const clickSide = Math.sign(clickIndex - originIndex);

  if (lastSide !== 0 && clickSide !== 0 && lastSide !== clickSide) {
    return originIndex;
  }

  return lastIndex;
};

const useShiftSelectionManager = <CollectionItem extends object>({
  collection,
  getId = defaultGetId,
  selectedIds,
}: UseShiftSelectionManagerOptions<CollectionItem>): ShiftSelectionAction => {
  const withShift = useRef(false);
  const lastClickedId = useRef<string | undefined>(undefined);
  const originId = useRef<string | undefined>(undefined);
  const selectedIdsRef = useRef(selectedIds);

  const clearLastIfSelectionIdle = () => {
    if (withShift.current) {
      return;
    }

    const currentSelectedIds = selectedIdsRef.current;
    if (!currentSelectedIds || currentSelectedIds.size > 0) {
      return;
    }

    lastClickedId.current = undefined;
    originId.current = undefined;
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        withShift.current = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        withShift.current = false;
        clearLastIfSelectionIdle();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
    clearLastIfSelectionIdle();
  }, [selectedIds, selectedIds?.size]);

  return useCallback(
    (id: string) => {
      if (!collection || !collection.length) {
        return [];
      }

      const index = collection.findIndex((item) => getId(item) === id);
      if (index === -1) {
        return [];
      }

      const ids = new Set<string>([id]);

      if (withShift.current && lastClickedId.current !== undefined) {
        const lastIndex = collection.findIndex((item) => getId(item) === lastClickedId.current);
        if (lastIndex !== -1) {
          const originIndex =
            originId.current === undefined
              ? undefined
              : collection.findIndex((item) => getId(item) === originId.current);
          const rangeStart = resolveRangeStartIndex(
            index,
            lastIndex,
            originIndex === -1 ? undefined : originIndex
          );
          const start = Math.min(index, rangeStart);
          const end = Math.max(index, rangeStart);

          for (let i = start; i <= end; i++) {
            const item = collection[i];
            if (item !== undefined) {
              ids.add(getId(item));
            }
          }

          lastClickedId.current = id;

          return Array.from(ids);
        }
      }

      lastClickedId.current = id;
      originId.current = id;

      return Array.from(ids);
    },
    [collection, getId]
  );
};

export default useShiftSelectionManager;
